/**
 * @file Shared dynamic-import retry boundary and asset-error helpers.
 */
import { captureFetchException } from '@@/bits/error_tracker'
import window from '@@/bits/global'

// Production hashed assets (`/(ui/)assets/*.js|css`) or Vite-dev URLs (`/ui-dev/*.{js,ts,tsx,vue,css}`).
const RECOVERABLE_ASSET_PATH =
  /\/(?:(?:ui\/)?assets\/[^?#\s'")]+\.(?:js|css)|ui-dev\/[^?#\s'")]+\.(?:js|ts|tsx|vue|css))/

// Trailing `?|#|$` stops the match from continuing into an unrelated path suffix.
const RECOVERABLE_ASSET_URL = new RegExp(`${RECOVERABLE_ASSET_PATH.source}(\\?|#|$)`)

const RELATIVE_ASSET_URL_IN_MESSAGE = new RegExp(`${RECOVERABLE_ASSET_PATH.source}(?:\\?[^'")\\s]*)?`)

function getAssetLoadErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (error != null && typeof error === 'object') {
    const assetError = error as { href?: unknown; message?: unknown; src?: unknown }
    if (typeof assetError.message === 'string') return assetError.message
    if (typeof assetError.href === 'string') return assetError.href
    if (typeof assetError.src === 'string') return assetError.src
  }
  return String(error ?? '')
}

function isRecoverableAssetUrl(url: string | undefined): boolean {
  return typeof url === 'string' && RECOVERABLE_ASSET_URL.test(url)
}

function getAssetUrlFromAssetLoadError(error: unknown): string | undefined {
  if (error != null && typeof error === 'object') {
    const assetError = error as { href?: unknown; src?: unknown }
    if (typeof assetError.href === 'string' && isRecoverableAssetUrl(assetError.href)) return assetError.href
    if (typeof assetError.src === 'string' && isRecoverableAssetUrl(assetError.src)) return assetError.src
  }

  const message = getAssetLoadErrorMessage(error)
  const absoluteUrlMatches = message.match(/https?:\/\/[^\s'")]+/g) ?? []
  const absoluteAssetUrl = absoluteUrlMatches.find(isRecoverableAssetUrl)
  if (absoluteAssetUrl != null) return absoluteAssetUrl

  return message.match(RELATIVE_ASSET_URL_IN_MESSAGE)?.[0]
}

function isDynamicAssetLoadError(error: unknown): boolean {
  const message = getAssetLoadErrorMessage(error)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Failed to fetch module script') ||
    message.includes('Failed to load module script') ||
    message.includes('Load failed for module') ||
    (getAssetUrlFromAssetLoadError(error) != null && /(fetch|import|module|load)/i.test(message))
  )
}

function cacheBustAssetUrl(url: string): string {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}padlet-js-retry=${Date.now()}`
}

function importCacheBustedAsset<T>(url: string): Promise<T> {
  return import(/* @vite-ignore */ url) as Promise<T>
}

// A retry imports the chunk URL directly, bypassing the export-name mapping the bundler applies
// at the original import site. When the chunk's names are minified (`export{o as n,c as t}`) the
// namespace is not what the caller destructures, so treat it as a failed retry.
function isUsableNamespace(importedModule: unknown): boolean {
  if (importedModule == null || typeof importedModule !== 'object') return false
  const exportNames = Object.keys(importedModule)
  // A wrapper chunk exports only mangled <=2-char names. A caller-facing namespace has at least one readable name.
  return exportNames.some((exportName) => exportName.length > 2)
}

function importWithRetry<T>(
  loader: () => Promise<T>,
  retryImport: (url: string) => Promise<T> = importCacheBustedAsset,
): Promise<T> {
  return loader().catch((firstError: unknown) => {
    // Non-asset failures are not retryable; surface them immediately.
    if (!isDynamicAssetLoadError(firstError)) {
      throw firstError
    }

    return new Promise<T>((resolve, reject) => {
      // Pause so a transient CDN/network blip can clear, then retry once.
      setTimeout(() => {
        const failedUrl = getAssetUrlFromAssetLoadError(firstError)
        // A failed import() is stored in the HTML module map, so a second
        // loader() call of the same specifier replays the rejection with no
        // network request. Retry through a unique query string instead.
        const retry = failedUrl != null ? retryImport(cacheBustAssetUrl(failedUrl)) : loader()
        retry
          .then((importedModule) => {
            if (!isUsableNamespace(importedModule)) {
              // The chunk loaded but its namespace is unusable; give up as if the retry failed.
              captureFetchException(firstError, {
                source: 'dynamicImportRetry',
                outcome: 'retry_export_mismatch',
              })
              window.PadletAssetLoadRecovery?.recover?.(
                'dynamic-import-post-mount',
                getAssetUrlFromAssetLoadError(firstError),
                getAssetLoadErrorMessage(firstError),
              )
              reject(firstError)
              return
            }

            // Retry recovered the module; report the first failure without surfacing it.
            captureFetchException(
              firstError,
              {
                source: 'dynamicImportRetry',
                outcome: 'retry_succeeded',
              },
              { treatAsNetworkFailure: true },
            )
            resolve(importedModule)
          })
          .catch((retryError: unknown) => {
            // Second failure: hand off to page-level recovery instead of retrying again.
            window.PadletAssetLoadRecovery?.recover?.(
              'dynamic-import-post-mount',
              getAssetUrlFromAssetLoadError(retryError),
              getAssetLoadErrorMessage(retryError),
            )
            reject(retryError)
          })
      }, 1000)
    })
  })
}

export {
  getAssetLoadErrorMessage,
  getAssetUrlFromAssetLoadError,
  importWithRetry,
  isDynamicAssetLoadError,
  isRecoverableAssetUrl,
}
